import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  ArrowLeft,
  Building2,
  User,
  Phone,
  Mail,
  Globe,
  MapPin,
  Calendar,
  Sparkles,
  Activity,
  Edit,
  Save,
  X,
  FileText,
  Lightbulb,
  AlertTriangle,
  MessageSquare,
  CheckCircle2,
  DollarSign,
  Shield,
  ArrowRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_COLORS = {
  new_lead: '#64748B',
  contacted: '#3B82F6',
  demo_scheduled: '#0071E3',
  demo_completed: '#F59E0B',
  qualified: '#8B5CF6',
  payment_requested: '#EC4899',
  payment_approved: '#10B981',
  closed_won: '#10B981',
  closed_lost: '#EF4444'
};

const LeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders, isAdmin } = useAuth();
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [newActivity, setNewActivity] = useState('');
  const [enriching, setEnriching] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchLead();
    fetchActivities();
    fetchStages();
  }, [id]);

  const fetchLead = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/leads/${id}`, getAuthHeaders());
      setLead(response.data);
      setEditData(response.data);
    } catch (error) {
      toast.error('Failed to load lead');
      navigate('/leads');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivities = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/leads/${id}/activities`, getAuthHeaders());
      setActivities(response.data);
    } catch (error) {
      console.error('Failed to load activities:', error);
    }
  };

  const fetchStages = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/pipeline-stages`, getAuthHeaders());
      setStages(response.data);
    } catch (error) {
      console.error('Failed to load stages:', error);
    }
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API_URL}/api/leads/${id}`, editData, getAuthHeaders());
      toast.success('Lead updated');
      setEditing(false);
      fetchLead();
    } catch (error) {
      toast.error('Failed to update lead');
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.put(`${API_URL}/api/leads/${id}`, { status: newStatus }, getAuthHeaders());
      toast.success('Status updated');
      fetchLead();
      fetchActivities();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleEnrich = async () => {
    setEnriching(true);
    try {
      await axios.post(`${API_URL}/api/leads/${id}/enrich`, {}, getAuthHeaders());
      toast.success('Lead enriched with AI insights');
      fetchLead();
    } catch (error) {
      toast.error('AI enrichment failed');
    } finally {
      setEnriching(false);
    }
  };

  const handleAddActivity = async () => {
    if (!newActivity.trim()) return;
    
    try {
      await axios.post(`${API_URL}/api/leads/${id}/activities`, {
        lead_id: id,
        activity_type: 'note_added',
        description: newActivity
      }, getAuthHeaders());
      toast.success('Activity added');
      setNewActivity('');
      fetchActivities();
    } catch (error) {
      toast.error('Failed to add activity');
    }
  };

  const handleCompleteDemo = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/leads/${id}/complete-demo`, {}, getAuthHeaders());
      toast.success('Demo marked as completed');
      fetchLead();
      fetchActivities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark demo complete');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAdminQualify = async () => {
    if (!window.confirm('Qualify this lead? This will allow the agent to request payment.')) return;
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/leads/${id}/admin-qualify`, {}, getAuthHeaders());
      toast.success('Lead qualified! Agent can now request payment.');
      fetchLead();
      fetchActivities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to qualify lead');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestPayment = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/leads/${id}/request-payment`, {}, getAuthHeaders());
      toast.success('Payment request submitted!');
      fetchLead();
      fetchActivities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request payment');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprovePayment = async () => {
    if (!window.confirm('Approve this payment request?')) return;
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/leads/${id}/approve-payment`, {}, getAuthHeaders());
      toast.success('Payment approved!');
      fetchLead();
      fetchActivities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve payment');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-500">Loading lead...</div>
      </div>
    );
  }

  if (!lead) return null;

  return (
    <div className="space-y-6" data-testid="lead-detail">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate('/leads')}
          className="rounded-full"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#1B365D] tracking-tight">
              {lead.organization_name}
            </h1>
            <Badge 
              style={{ backgroundColor: STAGE_COLORS[lead.status] + '20', color: STAGE_COLORS[lead.status] }}
              className="capitalize font-medium"
            >
              {lead.status?.replace('_', ' ')}
            </Badge>
          </div>
          <p className="text-slate-500 mt-1">{lead.contact_name}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleEnrich}
            disabled={enriching}
            className="rounded-full"
            data-testid="enrich-btn"
          >
            <Sparkles className={`w-4 h-4 mr-2 ${enriching ? 'animate-spin' : ''}`} />
            {enriching ? 'Enriching...' : 'AI Enrich'}
          </Button>
          {editing ? (
            <>
              <Button variant="outline" onClick={() => setEditing(false)} className="rounded-full">
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSave} className="bg-[#1B365D] hover:bg-[#1B365D]/90 rounded-full" data-testid="save-lead-btn">
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
            </>
          ) : (
            <Button onClick={() => setEditing(true)} className="bg-[#1B365D] hover:bg-[#1B365D]/90 rounded-full" data-testid="edit-lead-btn">
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contact Info Card */}
          <Card className="border-0 shadow-apple">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#1B365D]" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              {editing ? (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Contact Name</Label>
                    <Input
                      value={editData.contact_name || ''}
                      onChange={(e) => setEditData({ ...editData, contact_name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Organization Name</Label>
                    <Input
                      value={editData.organization_name || ''}
                      onChange={(e) => setEditData({ ...editData, organization_name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      value={editData.email || ''}
                      onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={editData.phone || ''}
                      onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Specialty</Label>
                    <Input
                      value={editData.specialty || ''}
                      onChange={(e) => setEditData({ ...editData, specialty: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Provider Type</Label>
                    <Input
                      value={editData.provider_type || ''}
                      onChange={(e) => setEditData({ ...editData, provider_type: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Website</Label>
                    <Input
                      value={editData.website || ''}
                      onChange={(e) => setEditData({ ...editData, website: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Current EHR</Label>
                    <Input
                      value={editData.current_ehr || ''}
                      onChange={(e) => setEditData({ ...editData, current_ehr: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>Address</Label>
                    <Input
                      value={editData.address || ''}
                      onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input
                      value={editData.city || ''}
                      onChange={(e) => setEditData({ ...editData, city: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Input
                      value={editData.state || ''}
                      onChange={(e) => setEditData({ ...editData, state: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>Notes</Label>
                    <Textarea
                      value={editData.notes || ''}
                      onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-6">
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500">Contact</p>
                      <p className="font-medium">{lead.contact_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500">Email</p>
                      <p className="font-medium">{lead.email || '-'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500">Phone</p>
                      <p className="font-medium">{lead.phone || '-'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500">Website</p>
                      <p className="font-medium">{lead.website || '-'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500">Location</p>
                      <p className="font-medium">
                        {[lead.city, lead.state].filter(Boolean).join(', ') || '-'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500">Specialty</p>
                      <p className="font-medium">{lead.specialty || '-'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500">Provider Type</p>
                      <p className="font-medium">{lead.provider_type || '-'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500">Current EHR</p>
                      <p className="font-medium">{lead.current_ehr || '-'}</p>
                    </div>
                  </div>
                  {lead.notes && (
                    <div className="col-span-2 mt-2">
                      <p className="text-sm text-slate-500">Notes</p>
                      <p className="font-medium mt-1 text-slate-700">{lead.notes}</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* AI Insights */}
          {(lead.ai_summary || lead.ai_pain_points?.length > 0 || lead.ai_talking_points?.length > 0) && (
            <Card className="border-0 shadow-apple bg-gradient-to-br from-[#1B365D]/5 to-[#0071E3]/5">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#0071E3]" />
                  AI Insights
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {lead.ai_summary && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-2">Summary</h4>
                    <p className="text-slate-600">{lead.ai_summary}</p>
                  </div>
                )}
                
                {lead.ai_pain_points?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                      Potential Pain Points
                    </h4>
                    <ul className="space-y-2">
                      {lead.ai_pain_points.map((point, index) => (
                        <li key={index} className="flex items-start gap-2 text-slate-600">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 flex-shrink-0" />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {lead.ai_talking_points?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                      <Lightbulb className="w-4 h-4 text-emerald-500" />
                      Talking Points
                    </h4>
                    <ul className="space-y-2">
                      {lead.ai_talking_points.map((point, index) => (
                        <li key={index} className="flex items-start gap-2 text-slate-600">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2 flex-shrink-0" />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Activity Timeline */}
          <Card className="border-0 shadow-apple">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Activity className="w-5 h-5 text-[#1B365D]" />
                Activity Timeline
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Add Activity */}
              <div className="flex gap-3 mb-6">
                <Textarea
                  placeholder="Add a note or activity..."
                  value={newActivity}
                  onChange={(e) => setNewActivity(e.target.value)}
                  className="min-h-[80px]"
                  data-testid="add-activity-input"
                />
                <Button 
                  onClick={handleAddActivity}
                  className="bg-[#1B365D] hover:bg-[#1B365D]/90"
                  data-testid="add-activity-btn"
                >
                  <MessageSquare className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-4">
                {activities.length > 0 ? (
                  activities.map(activity => (
                    <div 
                      key={activity.id}
                      className="flex gap-4 p-4 rounded-xl bg-slate-50"
                    >
                      <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
                        <Activity className="w-5 h-5 text-[#1B365D]" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-slate-900">{activity.description}</p>
                        <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                          <span>{activity.user_name}</span>
                          <span>•</span>
                          <span>{new Date(activity.created_at).toLocaleString()}</span>
                          <Badge variant="secondary" className="ml-2 text-xs capitalize">
                            {activity.activity_type.replace('_', ' ')}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-slate-500 py-8">No activities yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Lead Lifecycle Progress */}
          <Card className="border-0 shadow-apple" data-testid="lead-lifecycle">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <ArrowRight className="w-5 h-5 text-[#1B365D]" />
                Lead Lifecycle
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                {[
                  { id: 'new_lead', label: 'Submitted' },
                  { id: 'contacted', label: 'Contacted' },
                  { id: 'demo_scheduled', label: 'Demo Scheduled' },
                  { id: 'demo_completed', label: 'Demo Completed' },
                  { id: 'qualified', label: 'Qualified' },
                  { id: 'payment_requested', label: 'Payment Requested' },
                  { id: 'payment_approved', label: 'Payment Approved' },
                ].map((step, idx, arr) => {
                  const stageOrder = {
                    new_lead: 0, contacted: 1, demo_scheduled: 2, demo_completed: 3,
                    qualified: 4, payment_requested: 5, payment_approved: 6, closed_won: 7, closed_lost: -1
                  };
                  const currentOrder = stageOrder[lead.status] ?? -1;
                  const stepOrder = stageOrder[step.id];
                  const isComplete = currentOrder >= stepOrder;
                  const isCurrent = lead.status === step.id;
                  return (
                    <div key={step.id} className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                        isComplete ? 'bg-emerald-500 text-white' : isCurrent ? 'bg-[#0071E3] text-white ring-2 ring-[#0071E3]/30' : 'bg-slate-100 text-slate-400'
                      }`}>
                        {isComplete ? <CheckCircle2 className="w-4 h-4" /> : idx + 1}
                      </div>
                      <span className={`text-sm ${isComplete ? 'text-slate-900 font-medium' : isCurrent ? 'text-[#0071E3] font-semibold' : 'text-slate-400'}`}>
                        {step.label}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Action Buttons Based on Status */}
              <div className="mt-5 space-y-2">
                {lead.status === 'demo_scheduled' && (
                  <Button
                    className="w-full bg-amber-500 hover:bg-amber-600 rounded-full"
                    onClick={handleCompleteDemo}
                    disabled={actionLoading}
                    data-testid="complete-demo-btn"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Mark Demo Completed
                  </Button>
                )}
                {lead.status === 'demo_completed' && isAdmin && (
                  <Button
                    className="w-full bg-[#8B5CF6] hover:bg-[#7C3AED] rounded-full text-white"
                    onClick={handleAdminQualify}
                    disabled={actionLoading}
                    data-testid="admin-qualify-btn"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Verify & Qualify Lead
                  </Button>
                )}
                {lead.status === 'qualified' && !isAdmin && (
                  <Button
                    className="w-full bg-emerald-600 hover:bg-emerald-700 rounded-full text-white"
                    onClick={handleRequestPayment}
                    disabled={actionLoading}
                    data-testid="request-payment-btn"
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    Request Payment
                  </Button>
                )}
                {lead.status === 'qualified' && isAdmin && (
                  <p className="text-xs text-center text-slate-500 bg-slate-50 rounded-lg p-2">
                    Awaiting agent payment request
                  </p>
                )}
                {lead.status === 'payment_requested' && isAdmin && (
                  <Button
                    className="w-full bg-emerald-600 hover:bg-emerald-700 rounded-full text-white"
                    onClick={handleApprovePayment}
                    disabled={actionLoading}
                    data-testid="approve-payment-btn"
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    Approve Payment
                  </Button>
                )}
                {lead.status === 'payment_requested' && !isAdmin && (
                  <p className="text-xs text-center text-amber-600 bg-amber-50 rounded-lg p-2">
                    Payment request pending admin approval
                  </p>
                )}
                {lead.status === 'payment_approved' && (
                  <p className="text-xs text-center text-emerald-600 bg-emerald-50 rounded-lg p-2">
                    Payment approved
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Status */}
          <Card className="border-0 shadow-apple">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Pipeline Stage</CardTitle>
            </CardHeader>
            <CardContent>
              <Select value={lead.status} onValueChange={handleStatusChange}>
                <SelectTrigger data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {stages.map(stage => (
                    <SelectItem key={stage.id} value={stage.id}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: stage.color }} />
                        {stage.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Owner */}
          <Card className="border-0 shadow-apple">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Owner</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#1B365D] flex items-center justify-center text-white font-semibold">
                  {lead.owner_name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div>
                  <p className="font-medium">{lead.owner_name || 'Unknown'}</p>
                  <p className="text-sm text-slate-500">Assigned agent</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Metadata */}
          <Card className="border-0 shadow-apple">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-500">Created</p>
                  <p className="font-medium">{new Date(lead.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-500">Last Updated</p>
                  <p className="font-medium">{new Date(lead.updated_at).toLocaleDateString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LeadDetail;
